import {
  RiskFactorPerCancerDtoCancerType,
  UserRiskFactorsPerCancerDto,
  UserRiskFactorToCancersImpactDto,
} from '@/api/generated/model';
import useIsMobile from '@/hooks/use-is-mobile';
import { formatCancerTypeNameWithCapitalization } from '@/utils/cancer/types';
import { Drawer, IconX, useDisclosure } from '@cmpsr/components';

import { ButtonIcon, IconMenu2, Popover, Text } from '@cmpsr/components';
import CancerMenuItem from './components/cancer-menu-item/cancer-menu-item';

type CancerMenuProps = {
  activeTabIndex: number;
  riskFactorsByCancer?: UserRiskFactorsPerCancerDto;
  cancersByRiskFactor?: UserRiskFactorToCancersImpactDto;
  cancerListRef: React.RefObject<HTMLDivElement>;
  riskFactorListRef: React.RefObject<HTMLDivElement>;
  jumpToCancerCard: (
    cancerTypeKey: RiskFactorPerCancerDtoCancerType,
    cancerListRef: React.RefObject<HTMLDivElement>
  ) => void;
  jumpToRiskFactorCard: (riskFactorKey: string, riskFactorListRef: React.RefObject<HTMLDivElement>) => void;
};

export default function CancerMenu({
  activeTabIndex,
  riskFactorsByCancer,
  cancersByRiskFactor,
  cancerListRef,
  riskFactorListRef,
  jumpToCancerCard,
  jumpToRiskFactorCard,
}: CancerMenuProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <>
        <ButtonIcon
          variant="primary"
          size="s"
          width="55px"
          color="white"
          icon={<IconMenu2 />}
          aria-label={`Jump to ${activeTabIndex === 0 ? 'cancer' : 'risk factor'}`}
          onClick={onOpen}
        />
        <Drawer size="sm" isOpen={isOpen} placement="bottom" onClose={onClose} autoFocus={true}>
          <Drawer.Overlay />
          <Drawer.Content maxH="90vh" background="gradient-02">
            <Drawer.Header display="flex" alignItems="center" justifyContent="space-between">
              <Text variant="text-header-M" color="white">
                Jump to
              </Text>
              <ButtonIcon
                variant="accent"
                size="m"
                width="55px"
                borderRadius="radii-6"
                icon={<IconX />}
                onClick={() => {
                  onClose();
                }}
                aria-label="Close"
              />
            </Drawer.Header>
            <Drawer.Body display="flex" flexDirection="column" gap={1}>
              {activeTabIndex === 0
                ? riskFactorsByCancer?.factorsPerCancer.map((cancer) => (
                    <CancerMenuItem
                      key={cancer.cancerType}
                      text={formatCancerTypeNameWithCapitalization(cancer.cancerType)}
                      onClick={() => {
                        jumpToCancerCard(cancer.cancerType, cancerListRef);
                        onClose();
                      }}
                    />
                  ))
                : cancersByRiskFactor?.riskFactorsToCancersImpact.map((riskFactor) => (
                    <CancerMenuItem
                      key={riskFactor.id}
                      text={riskFactor.label}
                      onClick={() => {
                        jumpToRiskFactorCard(riskFactor.id, riskFactorListRef);
                        onClose();
                      }}
                    />
                  ))}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer>
      </>
    );
  }

  return (
    <Popover placement="bottom-end" closeOnBlur={true}>
      {({ onClose }) => (
        <>
          <Popover.Trigger>
            <ButtonIcon
              variant="primary"
              size="s"
              width="55px"
              color="white"
              icon={<IconMenu2 />}
              aria-label={`Jump to ${activeTabIndex === 0 ? 'cancer' : 'risk factor'}`}
            />
          </Popover.Trigger>
          <Popover.Content minW="200px" background="gradient.dark" border="none" borderRadius="radii-3">
            <Popover.Header borderColor="transparent" px={6} pt={4} pb={0}>
              <Text variant="text-header-M" color="white">
                Jump to
              </Text>
            </Popover.Header>
            <Popover.Body display="flex" flexDirection="column" p={6} gap={1} maxH="40vh" overflowY="auto">
              {activeTabIndex === 0
                ? riskFactorsByCancer?.factorsPerCancer.map((cancer) => (
                    <CancerMenuItem
                      key={cancer.cancerType}
                      text={formatCancerTypeNameWithCapitalization(cancer.cancerType)}
                      onClick={() => {
                        jumpToCancerCard(cancer.cancerType, cancerListRef);
                        onClose();
                      }}
                    />
                  ))
                : cancersByRiskFactor?.riskFactorsToCancersImpact.map((riskFactor) => (
                    <CancerMenuItem
                      key={riskFactor.id}
                      text={riskFactor.label}
                      onClick={() => {
                        jumpToRiskFactorCard(riskFactor.id, riskFactorListRef);
                        onClose();
                      }}
                    />
                  ))}
            </Popover.Body>
          </Popover.Content>
        </>
      )}
    </Popover>
  );
}
